import { apiFetch } from '@/lib/apiClient';
import { ExpandedSearchResponse } from '@/types/server/restaurantApiResponses';

/**
 * Shared transport for the search surface, kept out of both
 * `useRestaurantQueries` and `useRingConsumption` so the two can depend on it
 * without depending on each other.
 */

// Bounded lifetime for search requests. `apiFetch` (src/lib/apiClient.ts) only
// forwards method/body/params to axios — it has no per-request timeout knob,
// and the global apiClient default is out of scope for this fix. Racing the
// fetch against a timer ensures a hung request (e.g. server ingesting under
// Google Places quota exhaustion, connection never resolving) still settles
// the TanStack Query promise, so the query can reach `error` state instead of
// hanging in `pending` forever. Note: this does not abort the underlying
// in-flight axios request, only the promise this queryFn awaits.
export const SEARCH_REQUEST_TIMEOUT_MS = 30000;

export function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Search request timed out'));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * Fetch expanded search results from newly cached H3 rings.
 * Called when receiving an Ably 'search_update' signal.
 *
 * `open_now` is deliberately NOT a parameter here: the endpoint reads it
 * exclusively from the cached record the search was initiated with, so the
 * search's own identity can never be overridden mid-expansion. Server handles
 * deduplication — no client-side exclusion list.
 */
export async function getExpandedSearchResults(
  searchId: string,
  k: number
): Promise<ExpandedSearchResponse> {
  return withTimeout(
    apiFetch<ExpandedSearchResponse>(
      `/restaurants/search/${searchId}/expanded/`,
      { params: { k } }
    ),
    SEARCH_REQUEST_TIMEOUT_MS
  );
}
